import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    ScraperAttribute,
    ScraperAttributeValueSource,
    ScraperCategory,
    ScraperService,
} from '../types/scrapedServicesTypes';
import { ServiceScraperResponse } from '../types/serviceScraperResponseTypes';
import { useNotification } from '../../../contexts/NotificationsContext';
import { saveScrapedServices } from '../services/serviceScraping';
import { adaptScrapedServicesToState } from '../services/adapters/scrapedServicesAdapter';
import { FeedbackMessages } from '../../../utils/commonVariables/messages';
import {
    useZitoneoContent,
    useZitoneoHotelPresentation,
} from './ZitoneoContentContext';
import { zitoneoRoutes } from '../utils/zitoneoRoutes';
import useUserProfile from '../../../customHooks/useUserProfile';

interface ServiceScraperPanelContextType {
    scrapedData: ScraperCategory[];
    isOpen: boolean;
    isFullScreen: boolean;
    openPanelFromResponse: (response: ServiceScraperResponse) => void;
    closePanel: () => void;
    toggleFullScreen: () => void;
    setServiceChecked: (
        categoryId: number,
        serviceTypeId: number,
        checked: boolean
    ) => void;
    setAttributeChecked: (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        checked: boolean
    ) => void;
    setAttributeValue: (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        value: string,
        source?: ScraperAttributeValueSource
    ) => void;
    setAttributeSelectedSource: (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        source: ScraperAttributeValueSource
    ) => void;
    selectAll: () => void;
    clearSelection: () => void;
    saveChanges: (idSupplierHotel: string, idHotel: number) => Promise<void>;
    isLoadingPanel: boolean;
}

const ServiceScraperPanelContext = createContext<
    ServiceScraperPanelContextType | undefined
>(undefined);

/**
 * Provides the scraper review data, selection and panel state.
 * @param props - The children sharing the review state.
 * @returns The scraper panel context provider.
 */
export const ProviderServiceScraperPanel = ({
    children,
}: {
    children: ReactNode;
}) => {
    const history = useHistory();
    const location = useLocation();
    const { setErrorMessage, setSuccessMessage } = useNotification();
    const { IdSystemUser } = useUserProfile();
    const { applySavedHotelContent } = useZitoneoContent();
    const {
        hotel,
        serviceTypes,
        serviceCategories,
        serviceAttributes,
        hotelServices,
    } = useZitoneoHotelPresentation();
    const [scrapedData, setScrapedData] = useState<ScraperCategory[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isLoadingPanel, setIsLoadingPanel] = useState(false);
    const scopeKey = JSON.stringify([
        location.key,
        location.pathname,
        location.search,
        location.hash,
        hotel.id,
        hotel.idHotel,
    ]);
    // Returning with Back must not revive work from an earlier visit.
    const scope = useMemo(() => ({ key: scopeKey }), [scopeKey]);
    const activeScopeRef = useRef<typeof scope | null>(scope);
    activeScopeRef.current = scope;
    const resultScopeRef = useRef<typeof scope | null>(null);

    useEffect(() => {
        activeScopeRef.current = scope;
        resultScopeRef.current = null;
        setScrapedData([]);
        setIsOpen(false);
        setIsFullScreen(false);
        return () => {
            activeScopeRef.current = null;
        };
    }, [scope]);

    const openPanelFromResponse = (response: ServiceScraperResponse) => {
        if (
            activeScopeRef.current !== scope ||
            response.HotelId !== hotel.idHotel
        ) {
            throw new Error(
                'Scraper results do not belong to the current hotel and route.'
            );
        }
        const categories = adaptScrapedServicesToState(
            response,
            serviceTypes,
            serviceCategories,
            serviceAttributes,
            hotelServices
        );
        resultScopeRef.current = scope;
        setScrapedData(categories);
        setIsOpen(true);
        setIsFullScreen(false);
    };

    const closePanel = () => {
        resultScopeRef.current = null;
        setScrapedData([]);
        setIsOpen(false);
        setIsFullScreen(false);
    };

    const toggleFullScreen = () => {
        setIsFullScreen(previous => !previous);
    };

    const updateService = (
        categoryId: number,
        serviceTypeId: number,
        update: (service: ScraperService) => ScraperService
    ) => {
        setScrapedData(previous =>
            previous.map(category =>
                category.IdServiceCategory === categoryId
                    ? {
                          ...category,
                          Services: category.Services.map(service =>
                              service.IdServiceType === serviceTypeId
                                  ? update(service)
                                  : service
                          ),
                      }
                    : category
            )
        );
    };

    const updateAttribute = (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        update: (attribute: ScraperAttribute) => ScraperAttribute
    ) => {
        updateService(categoryId, serviceTypeId, service => ({
            ...service,
            AttributeDefinitions: service.AttributeDefinitions.map(attribute =>
                attribute.IdAttributeDefinition === attributeId
                    ? update(attribute)
                    : attribute
            ),
        }));
    };

    const setServiceChecked = (
        categoryId: number,
        serviceTypeId: number,
        checked: boolean
    ) => {
        updateService(categoryId, serviceTypeId, service => ({
            ...service,
            Checked: checked,
            AttributeDefinitions: service.AttributeDefinitions.map(
                attribute => ({ ...attribute, Checked: checked })
            ),
        }));
    };

    const setAttributeChecked = (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        checked: boolean
    ) => {
        updateAttribute(categoryId, serviceTypeId, attributeId, attribute => ({
            ...attribute,
            Checked: checked,
        }));
    };

    const setAttributeValue = (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        value: string,
        source?: ScraperAttributeValueSource
    ) => {
        updateAttribute(categoryId, serviceTypeId, attributeId, attribute => ({
            ...attribute,
            [source ?? 'Value']: value,
        }));
    };

    const setAttributeSelectedSource = (
        categoryId: number,
        serviceTypeId: number,
        attributeId: number,
        source: ScraperAttributeValueSource
    ) => {
        updateAttribute(categoryId, serviceTypeId, attributeId, attribute => ({
            ...attribute,
            SelectedValueSource: source,
        }));
    };

    const setAllChecked = (checked: boolean) => {
        setScrapedData(previous =>
            previous.map(category => ({
                ...category,
                Services: category.Services.map(service => ({
                    ...service,
                    Checked: checked,
                    AttributeDefinitions: service.AttributeDefinitions.map(
                        attribute => ({ ...attribute, Checked: checked })
                    ),
                })),
            }))
        );
    };

    const saveChanges = async (idSupplierHotel: string, idHotel: number) => {
        if (
            resultScopeRef.current !== scope ||
            activeScopeRef.current !== scope ||
            idSupplierHotel !== hotel.id ||
            idHotel !== hotel.idHotel
        ) {
            if (activeScopeRef.current === scope) {
                setErrorMessage(
                    FeedbackMessages.SCRAPER_RESULTS_SCOPE_MISMATCH
                );
            }
            return;
        }
        try {
            setIsLoadingPanel(true);
            const response = await saveScrapedServices(
                scrapedData,
                idHotel,
                parseInt(IdSystemUser, 10)
            );
            if (!response.data || response.status !== 201) {
                throw new Error('Could not save scraped services.');
            }
            if (activeScopeRef.current !== scope) {
                return;
            }
            applySavedHotelContent(response.data);
            setSuccessMessage(FeedbackMessages.COMMON_SUCCESS_UPDATE);
            closePanel();
            history.push(zitoneoRoutes.HOTEL_EDITOR_HOTEL(idSupplierHotel));
        } catch (error) {
            console.error('Could not save scraped services:', error);
            if (activeScopeRef.current === scope) {
                setErrorMessage(FeedbackMessages.COMMON_ERROR);
            }
        } finally {
            setIsLoadingPanel(false);
        }
    };

    return (
        <ServiceScraperPanelContext.Provider
            value={{
                scrapedData,
                isOpen: isOpen && resultScopeRef.current === scope,
                isFullScreen,
                openPanelFromResponse,
                closePanel,
                toggleFullScreen,
                setServiceChecked,
                setAttributeChecked,
                setAttributeValue,
                setAttributeSelectedSource,
                selectAll: () => setAllChecked(true),
                clearSelection: () => setAllChecked(false),
                saveChanges,
                isLoadingPanel,
            }}
        >
            {children}
        </ServiceScraperPanelContext.Provider>
    );
};

/**
 * Reads the scraper panel state and review actions.
 * @returns The context supplied by ProviderServiceScraperPanel.
 */
export const useServiceScraperPanel = () => {
    const context = useContext(ServiceScraperPanelContext);
    if (!context) {
        throw new Error(
            'useServiceScraperPanel must be used within ProviderServiceScraperPanel'
        );
    }
    return context;
};
